/**
 * Follow System — Users can follow other lifters.
 * Feed shows workouts from followed users + gym mates.
 *
 * Uses existing `follows` table if present, otherwise
 * falls back gracefully.
 */

import { supabase } from './supabase';
import { createLogger } from './logger';
import { offlineActionQueue, isNetworkError } from './offlineSync';
import { isRateLimited } from './rateLimiter';
import { notifyNewFollower } from './notificationTriggers';

const log = createLogger('FOLLOW');

export interface FollowRelation {
  id: string;
  follower_id: string;
  following_id: string;
  created_at: string;
}

/** Follow a user (idempotent) */
export async function followUser(followerId: string, followingId: string): Promise<boolean> {
  if (isRateLimited('follow', 10, 60_000)) {
    throw new Error('Too many requests. Please wait a moment.');
  }
  if (followerId === followingId) return false;

  const { error } = await supabase
    .from('follows')
    .upsert(
      { follower_id: followerId, following_id: followingId },
      { onConflict: 'follower_id,following_id', ignoreDuplicates: true }
    );

  if (error) {
    if (isNetworkError(error)) {
      await offlineActionQueue.enqueueAction('follow', { followerId, followingId });
      log.info('followUser queued offline', { followerId, followingId });
      return true;
    }
    log.error('followUser failed:', error);
    return false;
  }

  // Fire-and-forget: notify followed user
  Promise.resolve(supabase.from('profiles').select('handle, display_name').eq('id', followerId).single()).then(({ data: p }) => {
    const name = p?.handle || p?.display_name || 'Someone';
    notifyNewFollower(followingId, name, followerId).catch(() => {});
  }).catch(() => {});

  return true;
}

/** Unfollow a user */
export async function unfollowUser(followerId: string, followingId: string): Promise<boolean> {
  if (isRateLimited('unfollow', 10, 60_000)) {
    throw new Error('Too many requests. Please wait a moment.');
  }
  const { error } = await supabase
    .from('follows')
    .delete()
    .eq('follower_id', followerId)
    .eq('following_id', followingId);

  if (error) {
    if (isNetworkError(error)) {
      await offlineActionQueue.enqueueAction('unfollow', { followerId, followingId });
      log.info('unfollowUser queued offline', { followerId, followingId });
      return true;
    }
    log.error('unfollowUser failed:', error);
    return false;
  }
  return true;
}

/** Check if user A follows user B */
export async function isFollowing(followerId: string, followingId: string): Promise<boolean> {
  try {
    const { count, error } = await supabase
      .from('follows')
      .select('id', { count: 'exact', head: true })
      .eq('follower_id', followerId)
      .eq('following_id', followingId);
    if (error) { log.error('isFollowing error:', error); return false; }
    return (count ?? 0) > 0;
  } catch (err) {
    log.error('isFollowing exception:', err);
    return false;
  }
}

/** Get follower count */
export async function getFollowerCount(userId: string): Promise<number> {
  try {
    const { count, error } = await supabase
      .from('follows')
      .select('id', { count: 'exact', head: true })
      .eq('following_id', userId);
    if (error) { log.error('getFollowerCount error:', error); return 0; }
    return count ?? 0;
  } catch (err) {
    log.error('getFollowerCount exception:', err);
    return 0;
  }
}

/** Get following count */
export async function getFollowingCount(userId: string): Promise<number> {
  try {
    const { count, error } = await supabase
      .from('follows')
      .select('id', { count: 'exact', head: true })
      .eq('follower_id', userId);
    if (error) { log.error('getFollowingCount error:', error); return 0; }
    return count ?? 0;
  } catch (err) {
    log.error('getFollowingCount exception:', err);
    return 0;
  }
}

/** Get list of user IDs that a user follows */
export async function getFollowingIds(userId: string): Promise<string[]> {
  try {
    const { data, error } = await supabase
      .from('follows')
      .select('following_id')
      .eq('follower_id', userId);
    if (error) { log.error('getFollowingIds error:', error); return []; }
    return (data ?? []).map(r => r.following_id);
  } catch (err) {
    log.error('getFollowingIds exception:', err);
    return [];
  }
}

/** Get followers list with profile info */
export async function getFollowers(userId: string, limit = 50): Promise<any[]> {
  try {
    const { data, error } = await supabase
      .from('follows')
      .select('follower_id, profiles!follows_follower_id_fkey(id, handle, display_name, avatar_url)')
      .eq('following_id', userId)
      .limit(limit);
    if (error) { log.error('getFollowers error:', error); return []; }
    return data ?? [];
  } catch (err) {
    log.error('getFollowers exception:', err);
    return [];
  }
}

/** Get following list with profile info */
export async function getFollowing(userId: string, limit = 50): Promise<any[]> {
  try {
    const { data, error } = await supabase
      .from('follows')
      .select('following_id, profiles!follows_following_id_fkey(id, handle, display_name, avatar_url)')
      .eq('follower_id', userId)
      .limit(limit);
    if (error) { log.error('getFollowing error:', error); return []; }
    return data ?? [];
  } catch (err) {
    log.error('getFollowing exception:', err);
    return [];
  }
}
